/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { memo } from 'react'
import styled, { keyframes } from 'styled-components'
import TimingFunctions from '../../utils/helpers/timingFunctions'

const fragmentTransform = 'translate(-100%, -100%)'
const fragmentTlTransform = `${fragmentTransform} translate(-3px, -3px)`
const fragmentTrTransform = `${fragmentTransform} translate(3px, -3px) rotate(90deg)`
const fragmentBlTransform = `${fragmentTransform} translate(-3px, 3px) rotate(-90deg)`
const fragmentBrTransform = `${fragmentTransform} translate(3px, 3px) rotate(180deg)`

const circleKeyframes = keyframes`
  10% { transform: scale(0.9); }
  12.5% { transform: scale(0.85); }
  15% { transform: scale(0.9); }
  0%, 100% { transform: scale(0.8); }
`

const afterFragmentKeyframes = keyframes`
  20% { background: linear-gradient(140deg, rgba(255, 255, 255, 0.6) 25%, rgba(255, 255, 255, 0) 75%), #91D8F6; }
  0%, 40% { background: #004186; }
`

const createFragmentKeyframes = (fragmentTransformValue: any) => keyframes`
  20% { transform: ${fragmentTransformValue} scale(0.75); }
  0%, 40% { transform: ${fragmentTransformValue}; }
`

interface LoaderProps {
  visible: boolean
  className: string
  children?: any
}

const Loader = styled(({ ...props }: LoaderProps) => {
  if (!props.visible) return null

  return (
    <div className={props.className}>
      <div className={`loader-wrapper ${props.visible ? 'visible' : ''}`}>
        <div className={`loader fixed ${props.visible ? 'visible' : ''}`}>
          <span className="circle">
            <span className="brand-fragment fragment-tl" />
            <span className="brand-fragment fragment-tr" />
            <span className="brand-fragment fragment-bl" />
            <span className="brand-fragment fragment-br" />
          </span>
        </div>
        {props.children}
      </div>
      <div className={`backdrop ${props.visible ? 'visible' : ''}`} />
    </div>
  )
})`
  .loader-wrapper {
    position: fixed;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 999;
    flex-direction: column;
    transition: all 500ms ${TimingFunctions.easeOut.back};

    &:not(.visible) {
      opacity: 0;
      pointer-events: none;
    }
  }

  .backdrop {
    position: fixed;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    z-index: 998;
    background: rgba(229, 229, 229, 0.8);
    transition: all 500ms ${TimingFunctions.easeOut.back};

    &:not(.visible) {
      opacity: 0;
      pointer-events: none;
    }
  }

  .loader {
    position: relative;
    width: 150px;
    height: 150px;
    transition: all 500ms ${TimingFunctions.easeOut.back};

    &::after {
      content: '';
      position: absolute;
      background: #000;
      width: 100%;
      height: 100%;
      z-index: -1;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      border-radius: 100%;
      filter: blur(20px);
      opacity: 0.2;
    }

    &:not(.visible) {
      opacity: 0;
      pointer-events: none;
    }

    .circle {
      width: 100%;
      height: 100%;
      display: inline-flex;
      position: absolute;
      top: 0;
      left: 0;
      padding: 20px;
      border-radius: 100px;
      box-shadow: 0px 10px 40px rgba(0, 0, 0, 0.08);
      background: #fff;
      animation: ${circleKeyframes} 3s ${TimingFunctions.easeOut.back} infinite;

      .brand-fragment {
        position: absolute;
        background: #fff padding-box;
        width: 33.3%;
        height: 33.3%;
        border: solid transparent;
        border-width: 0 16px 16px 0;
        box-sizing: border-box;
        transform-origin: 100% 100%;
        top: 50%;
        left: 50%;
        animation: 4s ${TimingFunctions.easeOut.expo} infinite;

        &.fragment-tl {
          transform: ${fragmentTlTransform};
          animation-name: ${createFragmentKeyframes(fragmentTlTransform)};
          animation-delay: 1s;

          &::after {
            animation-delay: 1s;
          }
        }

        &.fragment-tr {
          transform: ${fragmentTrTransform};
          animation-name: ${createFragmentKeyframes(fragmentTrTransform)};
          animation-delay: 2s;

          &::after {
            animation-delay: 2s;
          }
        }

        &.fragment-bl {
          transform: ${fragmentBlTransform};
          animation-name: ${createFragmentKeyframes(fragmentBlTransform)};
          animation-delay: 4s;

          &::after {
            animation-delay: 4s;
          }
        }

        &.fragment-br {
          transform: ${fragmentBrTransform};
          animation-name: ${createFragmentKeyframes(fragmentBrTransform)};
          animation-delay: 3s;

          &::after {
            animation-delay: 3s;
          }
        }

        &::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: #fff;
        }

        &::after {
          content: '';
          position: absolute;
          z-index: -1;
          top: 1px;
          left: 1px;
          bottom: -17px;
          right: -17px;
          background: #004186;
          animation: ${afterFragmentKeyframes} 4s
            ${TimingFunctions.easeOut.expo} infinite;
        }
      }
    }
  }
`

export default memo(Loader)
